import { useEffect, useRef, useState } from "react";
import { MessageResponse } from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { adminTheme } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { MessageAdminController } from "api/main/MessageAdminController";
import { MessagePublicController } from "api/main/MessagePublicController";
import * as yup from "yup";
import { useFormik } from "formik";

const validationSchema = yup.object({
  subject: yup.string().required("Tema yra privaloma."),
  content: yup.string().required("Pranešimas yra privalomas."),
});

export default function MessagesListPage() {
  const api = useRef(createApi("messages") as MessageAdminController);
  const apiPublic = useRef(createApi("messagespublic") as MessagePublicController);
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<MessageResponse | null>(null);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    getData();
  }, []);

  async function getData() {
    setLoading(true);
    const data = await apiPublic.current.getAll();
    setMessages(data || []);
    setLoading(false);
  }

  const formik = useFormik({
    initialValues: {
      subject: "",
      content: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setSubmitting(true);
      try {
        await api.current.save(values as any);
        await getData();
        setCreateDialogOpen(false);
        resetForm();
      } catch (error) {
        console.error("Klaida siunčiant pranešimą:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    formik.resetForm();
    setPreviewMode(false);
  };

  const handleViewMessage = (message: MessageResponse) => {
    setSelectedMessage(message);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedMessage(null);
  };

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100%" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Pranešimų kūrimas
            </Typography>
            <Typography
              variant="body"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              Kurkite ir valdykite pranešimus vartotojams
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              background: adminTheme.gradients.primary,
              "&:hover": { background: adminTheme.gradients.primaryHover },
              px: 3,
              py: 1.5,
            }}
          >
            Naujas pranešimas
          </Button>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Iš viso pranešimų
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {messages.length}
            </Typography>
          </Card>
        </Box>

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell>Nr.</TableCell>
                  <TableCell>Pavadinimas</TableCell>
                  <TableCell>Žinutė</TableCell>
                  <TableCell>Sukurta</TableCell>
                  <TableCell align="right">Veiksmai</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <NotificationsIcon
                          sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                        />
                        <Typography>Nėra pranešimų</Typography>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setCreateDialogOpen(true)}
                          sx={{ mt: 2 }}
                        >
                          Sukurti pirmą pranešimą
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  messages.map((message) => (
                    <TableRow
                      key={message.id}
                      sx={{
                        transition: adminTheme.transitions.fast,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.04)",
                        },
                      }}
                      onClick={() => handleViewMessage(message)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: adminTheme.colors.textMuted }}
                        >
                          {message.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: adminTheme.borderRadius.sm,
                              bgcolor: "rgba(99, 102, 241, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: adminTheme.colors.primary,
                            }}
                          >
                            <NotificationsIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: adminTheme.colors.textPrimary,
                            }}
                          >
                            {message.subject}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: adminTheme.colors.textSecondary,
                            maxWidth: 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {message.content?.replace(/<[^>]*>/g, '').substring(0, 100)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: adminTheme.colors.textMuted }}
                        >
                          {message.dateCreated}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Peržiūrėti">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewMessage(message);
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Create Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: adminTheme.borderRadius.lg,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              borderBottom: `1px solid ${adminTheme.colors.border}`,
            }}
          >
            Naujo pranešimo kūrimas
          </DialogTitle>
          <form onSubmit={formik.handleSubmit}>
            <DialogContent sx={{ pt: 3 }}>
              <TextField
                fullWidth
                label="Pranešimo pavadinimas"
                name="subject"
                value={formik.values.subject}
                onChange={formik.handleChange}
                error={formik.touched.subject && Boolean(formik.errors.subject)}
                helperText={formik.touched.subject && formik.errors.subject}
                disabled={submitting}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => setPreviewMode(!previewMode)}
                  disabled={submitting}
                >
                  {previewMode ? "Kodas" : "Peržiūra"}
                </Button>
              </Box>
              {previewMode ? (
                <Box
                  sx={{
                    border: `1px solid ${adminTheme.colors.border}`,
                    borderRadius: adminTheme.borderRadius.sm,
                    p: 2,
                    minHeight: 200,
                  }}
                  dangerouslySetInnerHTML={{ __html: formik.values.content }}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Pranešimas (HTML)"
                  name="content"
                  value={formik.values.content}
                  onChange={formik.handleChange}
                  error={formik.touched.content && Boolean(formik.errors.content)}
                  helperText={formik.touched.content && formik.errors.content}
                  disabled={submitting}
                  multiline
                  rows={8}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: `1px solid ${adminTheme.colors.border}` }}>
              <Button onClick={handleCloseDialog} disabled={submitting}>
                Atšaukti
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{
                  background: adminTheme.gradients.primary,
                  "&:hover": { background: adminTheme.gradients.primaryHover },
                  minWidth: 150,
                }}
              >
                {submitting ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Siunčiama...
                  </Box>
                ) : (
                  "Sukurti pranešimą"
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* View Message Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: adminTheme.borderRadius.lg,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              borderBottom: `1px solid ${adminTheme.colors.border}`,
            }}
          >
            {selectedMessage?.subject}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
                Sukurta: {selectedMessage?.dateCreated}
              </Typography>
            </Box>
            <Box
              sx={{
                border: `1px solid ${adminTheme.colors.border}`,
                borderRadius: adminTheme.borderRadius.sm,
                p: 3,
                bgcolor: adminTheme.colors.background,
                minHeight: 200,
              }}
              dangerouslySetInnerHTML={{ __html: selectedMessage?.content || "" }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${adminTheme.colors.border}` }}>
            <Button onClick={handleCloseViewDialog}>Uždaryti</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
